/**
 * Document "scan look" enhancement for straightened invoice photos.
 *
 * [Fable 5.1 · Scan-look enhancer] — agreed with the product owner 2026-10-27.
 * Locked reference implementation. Do not replace with a global threshold,
 * CSS filter, or canvas `contrast()/brightness()` — those fail under shadows.
 *
 * Fable 5.1 DESIGN INVARIANTS
 *   1. Illumination is estimated locally (grid max-filter → blur → upsample),
 *      so a shadow across the sheet still ends as white paper. Global
 *      thresholds are not acceptable.
 *   2. The background map is floored at `bgFloorFrac` × global paper
 *      brightness. Large solid areas (logos, stamps, QR, filled headers) must
 *      not be hollowed out to white.
 *   3. Tone curve is on the luma / background ratio: ≥ whitePoint → 255,
 *      ≤ blackPoint → 0, gamma > 1 between. Paper is clipped to pure white.
 *   4. Colour is preserved by a per-channel gain (capped at `maxGain`), never
 *      by converting to greyscale. Stamps and logos keep their hue.
 *   5. Sharpening skips pure-white pixels so paper stays noise-free.
 *   6. Runs after the perspective warp and before JPEG encode. The original
 *      camera frame is still uploaded as a training sidecar; `enhanced` +
 *      `enhance_version` are recorded in corners.json.
 *   7. Default ON in the straighten dialog, with a visible toggle; the raw warp
 *      is cached so the toggle never re-runs the perspective pass.
 *   8. Regression tests: invoiceEnhance.test.js (shadowed paper ≥ 245, ink
 *      < 90, red stamp keeps hue, input buffer not mutated).
 *
 * Mirrors what CamScanner-style apps call Magic Color: flatten uneven lighting
 * so paper becomes white everywhere (also under shadows), then stretch the tone
 * curve so ink goes crisp black while coloured stamps / logos keep their hue.
 *
 * Pipeline (pure JS on an {width, height, data} RGBA buffer):
 *   1. Luma → coarse grid (≈ CELL px per cell).
 *   2. Background map = grid max-filter (ink is dark, paper survives) → box
 *      blur → bilinear upsample. This is the illumination / paper estimate.
 *   3. Normalise: n = luma / background  (≈1 on paper, <1 on ink).
 *   4. Tone curve: n ≥ WHITE_POINT → white; n ≤ BLACK_POINT → black; in
 *      between a gamma > 1 deepens ink without fattening thin strokes.
 *   5. Per-channel gain = target / luma, so hue is preserved; light 3×3
 *      unsharp for crispness.
 *
 * Runs after the perspective warp (the image is only the sheet by then) and
 * before JPEG encode. Heavy OCR uses the enhanced file; the original camera
 * frame is still uploaded as a training sidecar.
 */

export const ENHANCE_VERSION = "v1-magicColor";

export const ENHANCE_PARAMS = Object.freeze({
  /** Target long side of the coarse grid used for the background map. */
  gridLongSide: 320,
  /** Max-filter radius in grid cells (must exceed half a glyph height). */
  maxRadiusCells: 5,
  /** Blur radius in grid cells after the max filter. */
  blurRadiusCells: 3,
  /** luma / background ratio at or above this is pure white paper. */
  whitePoint: 0.9,
  /** Ratio at or below this is solid ink. */
  blackPoint: 0.42,
  /** > 1 darkens mid-greys (anti-aliased stroke edges) toward ink. */
  gamma: 1.35,
  /** Unsharp mask amount (0 disables). */
  sharpen: 0.45,
  /** Clamp per-channel gain so colour noise on paper cannot explode. */
  maxGain: 3.0,
  /**
   * Local background may never drop below this fraction of the global paper
   * brightness. Stops large solid areas (logos, stamps, filled headers) from
   * being mistaken for "dark paper" and hollowed out to white.
   */
  bgFloorFrac: 0.5,
  /** Percentile of luma treated as global paper brightness. */
  paperPercentile: 0.92,
});

function clamp255(v) {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

/** Separable max filter on a W×H Float32 grid. */
function maxFilter(src, w, h, r) {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const row = y * w;
    for (let x = 0; x < w; x += 1) {
      let m = -Infinity;
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(w - 1, x + r);
      for (let k = x0; k <= x1; k += 1) {
        const v = src[row + k];
        if (v > m) m = v;
      }
      tmp[row + x] = m;
    }
  }
  for (let x = 0; x < w; x += 1) {
    for (let y = 0; y < h; y += 1) {
      let m = -Infinity;
      const y0 = Math.max(0, y - r);
      const y1 = Math.min(h - 1, y + r);
      for (let k = y0; k <= y1; k += 1) {
        const v = tmp[k * w + x];
        if (v > m) m = v;
      }
      out[y * w + x] = m;
    }
  }
  return out;
}

/** Separable box blur (mean) on a W×H Float32 grid. */
function boxBlur(src, w, h, r) {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const row = y * w;
    for (let x = 0; x < w; x += 1) {
      let s = 0;
      let n = 0;
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(w - 1, x + r);
      for (let k = x0; k <= x1; k += 1) {
        s += src[row + k];
        n += 1;
      }
      tmp[row + x] = s / n;
    }
  }
  for (let x = 0; x < w; x += 1) {
    for (let y = 0; y < h; y += 1) {
      let s = 0;
      let n = 0;
      const y0 = Math.max(0, y - r);
      const y1 = Math.min(h - 1, y + r);
      for (let k = y0; k <= y1; k += 1) {
        s += tmp[k * w + x];
        n += 1;
      }
      out[y * w + x] = s / n;
    }
  }
  return out;
}

/**
 * Estimate the paper brightness under every pixel.
 * Returns a Float32Array(W*H) in luma units (0..255).
 */
export function estimateBackground(luma, W, H, params = ENHANCE_PARAMS) {
  const cell = Math.max(1, Math.ceil(Math.max(W, H) / params.gridLongSide));
  const gw = Math.max(1, Math.ceil(W / cell));
  const gh = Math.max(1, Math.ceil(H / cell));
  const sum = new Float32Array(gw * gh);
  const cnt = new Float32Array(gw * gh);
  for (let y = 0; y < H; y += 1) {
    const gy = (y / cell) | 0;
    const row = y * W;
    const grow = gy * gw;
    for (let x = 0; x < W; x += 1) {
      const gi = grow + ((x / cell) | 0);
      sum[gi] += luma[row + x];
      cnt[gi] += 1;
    }
  }
  const grid = new Float32Array(gw * gh);
  for (let i = 0; i < grid.length; i += 1) grid[i] = cnt[i] ? sum[i] / cnt[i] : 255;

  const dil = maxFilter(grid, gw, gh, params.maxRadiusCells);
  const bgGrid = boxBlur(dil, gw, gh, params.blurRadiusCells);

  // Global paper brightness (high percentile of the cell means) → floor.
  const sorted = Float32Array.from(grid).sort();
  const paper = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * params.paperPercentile))];
  const floor = paper * params.bgFloorFrac;
  for (let i = 0; i < bgGrid.length; i += 1) {
    if (bgGrid[i] < floor) bgGrid[i] = floor;
  }

  // Bilinear upsample back to full resolution (cell centres → pixel).
  const bg = new Float32Array(W * H);
  for (let y = 0; y < H; y += 1) {
    const fy = Math.min(gh - 1, Math.max(0, (y + 0.5) / cell - 0.5));
    const y0 = fy | 0;
    const y1 = Math.min(gh - 1, y0 + 1);
    const ty = fy - y0;
    const row = y * W;
    for (let x = 0; x < W; x += 1) {
      const fx = Math.min(gw - 1, Math.max(0, (x + 0.5) / cell - 0.5));
      const x0 = fx | 0;
      const x1 = Math.min(gw - 1, x0 + 1);
      const tx = fx - x0;
      const a = bgGrid[y0 * gw + x0];
      const b = bgGrid[y0 * gw + x1];
      const c = bgGrid[y1 * gw + x0];
      const d = bgGrid[y1 * gw + x1];
      bg[row + x] = (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
    }
  }
  return bg;
}

/**
 * Enhance an RGBA buffer in place-safe fashion (returns a new buffer).
 * @param {{width:number,height:number,data:Uint8ClampedArray}} img
 */
export function enhanceDocumentImageData(img, params = ENHANCE_PARAMS) {
  const W = img.width;
  const H = img.height;
  const src = img.data;
  const N = W * H;
  const luma = new Float32Array(N);
  for (let i = 0, p = 0; i < N; i += 1, p += 4) {
    luma[i] = 0.299 * src[p] + 0.587 * src[p + 1] + 0.114 * src[p + 2];
  }
  const bg = estimateBackground(luma, W, H, params);

  const { whitePoint, blackPoint, gamma, maxGain } = params;
  const span = Math.max(1e-3, whitePoint - blackPoint);
  const target = new Float32Array(N);
  for (let i = 0; i < N; i += 1) {
    const b = Math.max(8, bg[i]);
    const n = luma[i] / b;
    let t;
    if (n >= whitePoint) t = 1;
    else if (n <= blackPoint) t = 0;
    else t = Math.pow((n - blackPoint) / span, gamma);
    target[i] = t * 255;
  }

  const out = new Uint8ClampedArray(N * 4);
  for (let i = 0, p = 0; i < N; i += 1, p += 4) {
    const l = luma[i];
    const t = target[i];
    if (t >= 254.5) {
      out[p] = 255;
      out[p + 1] = 255;
      out[p + 2] = 255;
    } else {
      const gain = Math.min(maxGain, t / Math.max(1, l));
      out[p] = clamp255(src[p] * gain);
      out[p + 1] = clamp255(src[p + 1] * gain);
      out[p + 2] = clamp255(src[p + 2] * gain);
    }
    out[p + 3] = 255;
  }

  if (params.sharpen > 0 && W >= 3 && H >= 3) {
    const amt = params.sharpen;
    const sharp = new Uint8ClampedArray(out);
    for (let y = 1; y < H - 1; y += 1) {
      for (let x = 1; x < W - 1; x += 1) {
        const p = (y * W + x) * 4;
        for (let c = 0; c < 3; c += 1) {
          const v = out[p + c];
          if (v === 255) continue; // paper stays paper
          const m =
            (out[p - W * 4 - 4 + c] + out[p - W * 4 + c] + out[p - W * 4 + 4 + c] +
              out[p - 4 + c] + v + out[p + 4 + c] +
              out[p + W * 4 - 4 + c] + out[p + W * 4 + c] + out[p + W * 4 + 4 + c]) / 9;
          sharp[p + c] = clamp255(v + amt * (v - m));
        }
      }
    }
    return { width: W, height: H, data: sharp };
  }
  return { width: W, height: H, data: out };
}
